import { useJotBillAccess } from '@features/billing/hooks/useJotBillAccess';
import { useGetIntakeOrgServicesQuery } from '@features/intake-hub/store/api';
import { FeatureGates, useFeatureGate } from '@services/statsig';

interface BillingSettingsAccess {
  /** Whether the practice may use the JotBill parts of Billing Settings. False while loading. */
  hasAccess: boolean;
  isLoading: boolean;
}

/**
 * JotBill access for Billing Settings only (decision 48, GDN-4206).
 *
 * Adds one source to `useJotBillAccess`: while the `intake_billing_stage` gate
 * is on, a practice that selected JotBill in intake can set up billing before
 * the `jotbill` gate or bundle entitlement is in place. Other JotBill surfaces
 * (claims, nav, notes) keep `useJotBillAccess`.
 */
export function useBillingSettingsAccess(): BillingSettingsAccess {
  const jotBill = useJotBillAccess();
  const intakeBillingStage = useFeatureGate(FeatureGates.INTAKE_BILLING_STAGE);
  const { currentData: orgServices, isLoading: servicesLoading } = useGetIntakeOrgServicesQuery(
    undefined,
    { skip: jotBill.hasAccess || !intakeBillingStage },
  );

  if (jotBill.hasAccess) return { hasAccess: true, isLoading: false };
  if (jotBill.isLoading || (intakeBillingStage && servicesLoading))
    return { hasAccess: false, isLoading: true };

  return {
    hasAccess: intakeBillingStage && Boolean(orgServices?.service_keys?.includes('jotbill')),
    isLoading: false,
  };
}
